// Tutorial — a non-blocking, in-match coach for new players. Lives entirely
// in DOM and reads MatchState snapshots to auto-advance: each step has a
// `predicate(state)` that returns true when the player completes the goal.
//
// V4.1 cohort restructure:
//   - Match 1 (`telemetry.length === 0`): three actionable steps + a
//     "you're ready" closer. The previous 5-step flow added an ability
//     step that was the steepest learning curve and the most likely
//     bail point for someone who'd never touched the game.
//   - Match 2 (`telemetry.length === 1`): a single ability coach step,
//     gated by `signalclash.tutorial.abilities_completed`. Deferring the
//     ability lesson means the player has already proven they understand
//     move + capture + build before the harder beat lands.
//   - Match 3+ (`telemetry.length >= 2` OR both flags set): tutorial
//     off.
//
// Every step also emits a tutorial_step_shown / tutorial_step_completed /
// tutorial_skipped / tutorial_completed analytics event so the dashboard
// can see exactly where the funnel drops in onboarding.

import type { MatchState, PlayerId } from '@signalclash/shared';

import { analytics } from '../analytics/Analytics.js';
import { isTouchProfile } from '../input/Profile.js';
import { loadRows as loadTelemetryRows } from '../save/Telemetry.js';

import { injectHudThemeStyles } from './hudThemeStyles.js';

const ROOT_ID = 'tutorial-root';
const STYLE_ID = 'tutorial-styles';
// Legacy "the whole tutorial is done" flag — kept for back-compat with
// the V1.7 5-step path. A returning player with this flag set never sees
// the tutorial again regardless of the cohort split below.
const STORAGE_KEY_COMPLETED = 'signalclash.tutorial.completed';
// V4.1 cohort flag — abilities coach (the deferred 4th-step). Set after
// the second-match Q-coach predicate fires (or is skipped).
const STORAGE_KEY_ABILITIES = 'signalclash.tutorial.abilities_completed';
// V1.9 cohort flag — macro coach (matches 3-9). Set when the player
// skips all macro toasts or completes the cohort (any match in the
// window finishes with no remaining toasts pending). Cross-device
// synced via CLOUD_SAVE_KEYS so the toasts don't re-fire on a phone
// after the player has already seen them on desktop.
const STORAGE_KEY_MACRO = 'signalclash.tutorial.macro_completed';
// V1.9 — passive macro toasts only run during matches 3 through this
// cap, then auto-mark the cohort complete. Keeps the in-match clutter
// budget bounded for veterans without surfacing "you've seen everything"
// state to the player.
const MACRO_COACH_LAST_MATCH = 9;

interface Step {
  id: string;
  title: string;
  body: string;
  // Returns true when the player has completed this step. Optional —
  // some steps auto-advance after a fixed time.
  predicate?: (state: MatchState, viewerId: PlayerId) => boolean;
  // Auto-advance after Nms even if predicate hasn't fired. Used for
  // the welcome step and the closer.
  autoAdvanceMs?: number;
  // V1.9 — macro coach steps gate on match-clock time, not actions. Each
  // step waits until `state.matchSeconds >= showAtSec` before rendering,
  // then auto-dismisses after `autoAdvanceMs`. Macro toasts are passive
  // (no Skip button, narrower card, doesn't claim attention) so they
  // can sit alongside other in-match UI without blocking input.
  showAtSec?: number;
  passive?: boolean;
}

type TutorialCohort = 'first_match' | 'abilities_coach' | 'macro_coach' | 'off';

// Determine which set of steps to show based on match count + persisted
// per-cohort flags. The Telemetry module owns the local match-row store;
// row count == matches played. Cloud-save hydration runs before the first
// tutorial check, so a returning player on a new device sees the right
// cohort.
//
// V1.9 — adds `macro_coach` for matches 3-9. The first/abilities cohorts
// covered "how to play"; macro covers "how to play *well*" with timed,
// non-blocking toasts (build a Hub, push when ahead, watch the Storm
// timer). Avg playtime is the metric to move: a player who knows what to
// do next in minute 3 stays for minute 4.
function chooseCohort(): TutorialCohort {
  if (alreadyCompletedAll()) return 'off';
  let matchCount = 0;
  try {
    matchCount = loadTelemetryRows().length;
  } catch {
    matchCount = 0;
  }
  if (matchCount === 0) return 'first_match';
  if (matchCount === 1 && !alreadyCompletedAbilities()) return 'abilities_coach';
  if (matchCount >= 2 && matchCount < MACRO_COACH_LAST_MATCH && !alreadyCompletedMacro()) {
    return 'macro_coach';
  }
  return 'off';
}

function alreadyCompletedAll(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_COMPLETED) === '1';
  } catch {
    return false;
  }
}

function alreadyCompletedAbilities(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_ABILITIES) === '1';
  } catch {
    return false;
  }
}

function alreadyCompletedMacro(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_MACRO) === '1';
  } catch {
    return false;
  }
}

function markCompletedAll(): void {
  try {
    localStorage.setItem(STORAGE_KEY_COMPLETED, '1');
  } catch {
    // localStorage unavailable; the tutorial will replay next session.
  }
}

function markCompletedAbilities(): void {
  try {
    localStorage.setItem(STORAGE_KEY_ABILITIES, '1');
  } catch {
    // see markCompletedAll
  }
}

function markCompletedMacro(): void {
  try {
    localStorage.setItem(STORAGE_KEY_MACRO, '1');
  } catch {
    // see markCompletedAll
  }
}

// Tutorial step bodies adapt to input modality. Desktop players read
// "Left-click / Right-click / Press B"; touch players read "Tap / Tap empty
// ground / Tap Build". The predicates are identical — the simulation doesn't
// care which input path produced the order. Computed once per `getSteps`
// invocation so the (cached) touch-profile check runs once per match.
function getStepsForCohort(cohort: TutorialCohort): Step[] {
  if (cohort === 'off') return [];
  const touch = isTouchProfile();
  if (cohort === 'first_match') {
    const verbSelectSquad = touch
      ? 'Tap one of your blue squads, then tap the ground to move it.'
      : 'Left-click one of your blue squads, then right-click on the ground to move it.';
    const verbMoveOnto = touch
      ? 'With a squad selected, tap a relay to move onto it. Hold position to capture it.'
      : 'Right-click to move your squad onto a relay. Hold position to capture it.';
    const verbBuild = touch
      ? 'Tap your Core, then tap any squad icon (Recon, Assault, Engineer, Shield) in the Core card to build it. Costs signal.'
      : 'Select your Core, then click any squad icon (Recon, Assault, Engineer, Shield) in the Core card to build it. Costs signal.';
    return [
      {
        id: 'select_squad',
        title: 'Step 1 — Move a squad',
        body: verbSelectSquad,
        predicate: (state, viewerId) => {
          for (const s of Object.values(state.squads)) {
            if (s.ownerId !== viewerId) continue;
            if (s.orderQueue.length > 0) return true;
          }
          return false;
        },
      },
      {
        id: 'capture',
        title: 'Step 2 — Capture a relay',
        body: verbMoveOnto,
        predicate: (state, viewerId) => {
          for (const node of Object.values(state.controlNodes)) {
            if (node.ownerId === viewerId) return true;
          }
          return false;
        },
      },
      {
        id: 'build',
        title: 'Step 3 — Build a squad',
        body: verbBuild,
        predicate: (state, viewerId) => {
          // Pre-spawned Recon makes squadCount start at 1 — advance once a
          // second squad is queued (queue length > 0) or has finished
          // production (squadCount > 1). Catching the queue lets the step
          // advance the moment the player commits to building.
          if (state.players[viewerId].squadCount > 1) return true;
          for (const core of Object.values(state.cores)) {
            if (core.ownerId !== viewerId) continue;
            if (core.produces !== null && core.produces.queue.length > 0) return true;
          }
          return false;
        },
      },
      {
        id: 'done',
        title: 'You\'re ready',
        body: 'Destroy the enemy Core or hold a relay majority into Overtime. Good luck!',
        autoAdvanceMs: 8000,
      },
    ];
  }
  // abilities_coach — single step for match 2. Players who skipped the
  // first-match tutorial entirely still get this coach because the
  // completion flag for it is per-cohort.
  if (cohort === 'abilities_coach') {
    const verbAbility = touch
      ? 'With a squad selected, tap the Q button in the bottom bar (e.g. Suppressing Fire).'
      : 'With a squad selected, press Q for its ability (e.g. Suppressing Fire).';
    return [
      {
        id: 'ability',
        title: 'New: Use an ability',
        body: verbAbility,
        predicate: (state, viewerId) => {
          for (const s of Object.values(state.squads)) {
            if (s.ownerId !== viewerId) continue;
            if (s.channelingAbility !== null) return true;
            if (s.abilityCooldown > 0) return true;
          }
          return false;
        },
      },
      {
        id: 'done',
        title: 'You\'re mastering this',
        body: 'Use abilities at the right moment to swing matches. Watch the cooldowns.',
        autoAdvanceMs: 8000,
      },
    ];
  }
  // V1.9 macro_coach — passive timed toasts during matches 3-9. Each is a
  // small bottom-of-screen card that fades in at a fixed match time and
  // dismisses on its own. No Skip button (it's already non-blocking and
  // brief); a hit of any non-trivial player action also auto-clears via
  // the predicate where one applies. Order matches in-match rhythm:
  //   45 s  — build a Hub (you should have ~30+ Signal saved by now)
  //   90 s  — push when you outnumber the enemy
  //   180 s — Signal Storm warning at ~3 min into a 6-min cap
  //   240 s — finish: aim at the enemy Core
  return [
    {
      id: 'macro_hub',
      title: 'Coach tip — Build a Hub',
      body: touch
        ? 'Tap your Core, then tap "Build Hub". Each Hub adds a second build queue so your squads compound.'
        : 'Click your Core and pick "Build Hub". A second build queue means you out-produce the enemy.',
      showAtSec: 45,
      autoAdvanceMs: 9000,
      passive: true,
      predicate: (state, viewerId) => {
        // Auto-clear once the player has actually built any building.
        for (const b of Object.values(state.buildings ?? {})) {
          if (b.ownerId === viewerId) return true;
        }
        return false;
      },
    },
    {
      id: 'macro_push',
      title: 'Coach tip — Push when ahead',
      body: 'When you outnumber the enemy 2+ squads, select your squads and right-click the enemy Core.',
      showAtSec: 90,
      autoAdvanceMs: 9000,
      passive: true,
    },
    {
      id: 'macro_storm',
      title: 'Coach tip — Signal Storm soon',
      body: 'Around 4 minutes, the Signal Storm starts. Income drops — bank Signal now and pre-build squads.',
      showAtSec: 180,
      autoAdvanceMs: 9000,
      passive: true,
    },
    {
      id: 'macro_finish',
      title: 'Coach tip — Finish the match',
      body: 'Push toward the enemy Core. A relay majority into Overtime also wins — pick your moment.',
      showAtSec: 240,
      autoAdvanceMs: 9000,
      passive: true,
    },
  ];
}

export class Tutorial {
  private root: HTMLDivElement | null = null;
  private currentStepIdx = 0;
  private viewerId: PlayerId = 0;
  private autoAdvanceTimer: ReturnType<typeof setTimeout> | null = null;
  private active = false;
  private cohort: TutorialCohort = 'off';
  private steps: Step[] = [];
  private completedStepIds: Set<string> = new Set();
  // V4.1 — analytics anchors. tutorialStartMs is fixed for the run, used
  // for tutorial_completed totalElapsedMs. stepShownAtMs resets each
  // step so per-step elapsedMs reflects time-in-step, not cumulative.
  private tutorialStartMs = 0;
  private stepShownAtMs = 0;
  // V1.9 — gate flag for macro-coach steps with `showAtSec`. Reset on
  // each step advance via advance() so the next macro step's gate engages
  // fresh against its own threshold.
  private macroShown = false;
  // V1.9 — objective pulse hooks injected by ClientApp. Tutorial calls
  // setObjective(x, y) during the first-match capture step to draw a
  // yellow ring on the target relay, then clearObjective() when the step
  // advances or the tutorial disposes. Optional — the tutorial still
  // works without the pulse, the player just doesn't get the highlight.
  private setObjective: ((worldX: number, worldY: number) => void) | null = null;
  private clearObjective: (() => void) | null = null;
  // Track the last pulsed relay id so we don't re-issue setTarget every
  // tick (cheap, but spammy in the console if devtools breakpoints fire).
  private lastPulsedNodeId: string | null = null;

  setViewerId(viewerId: PlayerId): void {
    this.viewerId = viewerId;
  }

  // V1.9 — wire the objective-pulse callbacks. ClientApp passes through
  // to the per-scene ObjectivePulseLayer; null pair removes any prior
  // wiring (e.g. between matches when the GameScene tore down).
  setObjectiveHooks(
    setObjective: ((worldX: number, worldY: number) => void) | null,
    clearObjective: (() => void) | null,
  ): void {
    this.setObjective = setObjective;
    this.clearObjective = clearObjective;
  }

  // Should be called by ClientApp at match-start. Picks the cohort and
  // mounts the appropriate step set; no-ops when the player is past the
  // tutorial cohorts.
  beginIfFirstMatch(): void {
    this.cohort = chooseCohort();
    if (this.cohort === 'off') return;
    this.steps = getStepsForCohort(this.cohort);
    if (this.steps.length === 0) return;
    this.active = true;
    this.currentStepIdx = 0;
    this.completedStepIds.clear();
    this.tutorialStartMs = Date.now();
    injectStyles();
    this.root = ensureRoot();
    this.renderCurrent();
  }

  // Called every snapshot tick. Cheap; bails immediately when inactive.
  observeSnapshot(state: MatchState): void {
    if (!this.active) return;
    const step = this.steps[this.currentStepIdx];
    if (step === undefined) {
      this.complete();
      return;
    }
    // V1.9 — macro coach gated rendering. A macro step with `showAtSec`
    // sits invisible until the match clock crosses the threshold. The
    // toast then renders (one render() call) and the existing
    // auto-advance timer pulls it off. Same observe-and-advance loop —
    // we just defer the first render.
    if (step.showAtSec !== undefined && !this.macroShown) {
      if (state.matchSeconds >= step.showAtSec) {
        this.macroShown = true;
        this.renderCurrent();
      } else {
        return;
      }
    }
    // V1.9 — first-match capture step: highlight the nearest neutral
    // relay so the player can't miss the objective. The pulse stays
    // until the step advances (predicate fires) or the tutorial
    // disposes. Cheap: only runs while on the 'capture' step.
    if (step.id === 'capture' && this.setObjective !== null) {
      this.updateCapturePulse(state);
    }
    if (step.predicate !== undefined && step.predicate(state, this.viewerId)) {
      this.advance();
    }
  }

  // V1.9 — find the closest neutral control node to the viewer's Core
  // and aim the pulse at it. Falls back to "any neutral relay" if no
  // own-Core position is available (e.g. spectator mode or a malformed
  // snapshot). No-op when no neutral relays remain on the map.
  private updateCapturePulse(state: MatchState): void {
    const setObj = this.setObjective;
    if (setObj === null) return;
    let ownCoreX: number | null = null;
    let ownCoreY: number | null = null;
    for (const core of Object.values(state.cores)) {
      if (core.ownerId === this.viewerId) {
        ownCoreX = core.pos.x;
        ownCoreY = core.pos.y;
        break;
      }
    }
    let bestId: string | null = null;
    let bestPos: { x: number; y: number } | null = null;
    let bestDistSq = Infinity;
    for (const node of Object.values(state.controlNodes)) {
      // Skip nodes already owned by the viewer — those don't need a pulse.
      if (node.ownerId === this.viewerId) continue;
      if (ownCoreX === null || ownCoreY === null) {
        // No reference core — first neutral we see wins.
        bestId = String(node.id);
        bestPos = { x: node.pos.x, y: node.pos.y };
        break;
      }
      const dx = node.pos.x - ownCoreX;
      const dy = node.pos.y - ownCoreY;
      const distSq = dx * dx + dy * dy;
      if (distSq < bestDistSq) {
        bestDistSq = distSq;
        bestId = String(node.id);
        bestPos = { x: node.pos.x, y: node.pos.y };
      }
    }
    if (bestPos === null || bestId === null) {
      // No neutral relays left — clear any stale pulse and bail.
      this.clearObjective?.();
      this.lastPulsedNodeId = null;
      return;
    }
    if (this.lastPulsedNodeId !== bestId) {
      this.lastPulsedNodeId = bestId;
      setObj(bestPos.x, bestPos.y);
    } else {
      // Same target — keep the existing tween running. The pulse layer
      // ignores idempotent setTarget calls already, so this branch just
      // avoids the function-call overhead on every tick.
    }
  }

  hide(): void {
    if (this.root !== null) this.root.style.display = 'none';
  }

  show(): void {
    if (this.root !== null && this.active) this.root.style.display = 'block';
  }

  // Permanently disable for this user (Skip button). The cohort decides
  // which storage flag to set — first-match skip flips everything off,
  // abilities-coach skip only flips the abilities flag, macro skip
  // ends the macro cohort entirely.
  skip(): void {
    const step = this.steps[this.currentStepIdx];
    if (step !== undefined) {
      analytics.emit({
        kind: 'tutorial_skipped',
        atStepId: step.id,
        elapsedMs: Math.max(0, Date.now() - this.stepShownAtMs),
      });
    }
    // V1.9 — skip is an explicit "stop coaching me" signal, so it DOES
    // mark macro_coach as completed (unlike natural per-match completion
    // which lets the toasts re-fire in the 3-9 window).
    if (this.cohort === 'macro_coach') {
      markCompletedMacro();
    } else {
      this.markCompletedForCohort();
    }
    this.dispose();
  }

  dispose(): void {
    this.active = false;
    if (this.autoAdvanceTimer !== null) clearTimeout(this.autoAdvanceTimer);
    this.autoAdvanceTimer = null;
    // V1.9 — drop the objective pulse before tearing down so a residual
    // ring doesn't hang on the relay after the player advances past the
    // capture step (or skips the tutorial entirely).
    this.clearObjective?.();
    this.lastPulsedNodeId = null;
    if (this.root !== null) this.root.remove();
    this.root = null;
  }

  private advance(): void {
    const step = this.steps[this.currentStepIdx];
    if (step !== undefined) {
      this.completedStepIds.add(step.id);
      analytics.emit({
        kind: 'tutorial_step_completed',
        stepId: step.id,
        elapsedMs: Math.max(0, Date.now() - this.stepShownAtMs),
      });
    }
    // V1.9 — clear any in-flight objective pulse the moment a step
    // ends. The next step (or the tutorial completion) decides whether
    // a new target is needed.
    if (step?.id === 'capture') {
      this.clearObjective?.();
      this.lastPulsedNodeId = null;
    }
    this.currentStepIdx += 1;
    if (this.autoAdvanceTimer !== null) {
      clearTimeout(this.autoAdvanceTimer);
      this.autoAdvanceTimer = null;
    }
    if (this.currentStepIdx >= this.steps.length) {
      this.complete();
      return;
    }
    const nextStep = this.steps[this.currentStepIdx];
    // V1.9 — macro coach: if the next step has `showAtSec`, hide the
    // card and wait for observeSnapshot to gate it in. Otherwise render
    // immediately as before.
    if (nextStep?.showAtSec !== undefined) {
      this.macroShown = false;
      if (this.root !== null) this.root.style.display = 'none';
      return;
    }
    this.macroShown = false;
    this.renderCurrent();
  }

  private complete(): void {
    analytics.emit({
      kind: 'tutorial_completed',
      totalElapsedMs: Math.max(0, Date.now() - this.tutorialStartMs),
    });
    this.markCompletedForCohort();
    // Brief fade-out before removing.
    if (this.root !== null) {
      this.root.classList.add('tutorial-root--out');
      setTimeout(() => this.dispose(), 320);
      return;
    }
    this.dispose();
  }

  // Cohort-aware "this run is done" flag. Match 1 success or skip flips
  // the master flag (so the first-match coach never re-shows even if the
  // player wipes localStorage and a stale telemetry row implies they're
  // a new player). Abilities-coach success or skip only flips its own
  // flag, leaving the master clear in case a future cohort wants to add
  // more.
  private markCompletedForCohort(): void {
    if (this.cohort === 'first_match') {
      markCompletedAll();
      // First-match completion implicitly clears the abilities-coach
      // requirement only when the legacy 5-step path had been seen.
      // V4.1 cohort split treats them independently — we want returning
      // players to see the new coach even if they cleared the OLD
      // tutorial flag.
    } else if (this.cohort === 'abilities_coach') {
      markCompletedAbilities();
    } else if (this.cohort === 'macro_coach') {
      // V1.9 — natural completion of the macro toasts does NOT mark the
      // cohort done. We want the toasts to fire each match in the 3-9
      // window (the cohort gate in chooseCohort already stops them at
      // match 9). The flag is only set when the player explicitly
      // skips one of the toasts via the optional Skip path, or when
      // chooseCohort drops them out of the window naturally.
    }
  }

  private renderCurrent(): void {
    if (this.root === null) return;
    const step = this.steps[this.currentStepIdx];
    if (step === undefined) return;
    this.stepShownAtMs = Date.now();
    analytics.emit({
      kind: 'tutorial_step_shown',
      stepId: step.id,
      stepIdx: this.currentStepIdx,
      totalSteps: this.steps.length,
    });
    const passive = step.passive === true;
    // V1.9 — passive macro toasts use a narrower card, no Skip button, no
    // step counter (the player has no idea what step they're on and
    // doesn't care). The class hook lets the CSS swap layouts. Skip is
    // intentionally available as a small "x" close button so a veteran
    // can still dismiss the whole macro cohort with one click.
    const cardClass = passive ? 'tutorial-card tutorial-card--passive' : 'tutorial-card';
    const headInner = passive
      ? `<span class="tutorial-card__chip">Tip</span>
         <button class="tutorial-card__close" type="button" aria-label="Dismiss tips">×</button>`
      : `<span class="tutorial-card__chip">Tutorial</span>
         <button class="tutorial-card__skip" type="button">Skip</button>`;
    const footer = passive
      ? ''
      : `<div class="tutorial-card__progress">${this.currentStepIdx + 1} / ${this.steps.length}</div>`;
    this.root.innerHTML = `
      <div class="${cardClass}">
        <div class="tutorial-card__head">
          ${headInner}
        </div>
        <div class="tutorial-card__title">${escapeHtml(step.title)}</div>
        <div class="tutorial-card__body">${escapeHtml(step.body)}</div>
        ${footer}
      </div>
    `;
    this.root.style.display = 'block';
    const skipBtn = this.root.querySelector<HTMLButtonElement>('.tutorial-card__skip');
    skipBtn?.addEventListener('click', () => this.skip());
    const closeBtn = this.root.querySelector<HTMLButtonElement>('.tutorial-card__close');
    closeBtn?.addEventListener('click', () => this.skip());
    if (this.autoAdvanceTimer !== null) {
      clearTimeout(this.autoAdvanceTimer);
      this.autoAdvanceTimer = null;
    }
    if (step.autoAdvanceMs !== undefined) {
      this.autoAdvanceTimer = setTimeout(() => this.advance(), step.autoAdvanceMs);
    }
  }
}

function ensureRoot(): HTMLDivElement {
  const existing = document.getElementById(ROOT_ID);
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = ROOT_ID;
  document.body.appendChild(div);
  return div;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${ROOT_ID} {
  position: fixed;
  left: 50%;
  bottom: 110px;
  transform: translateX(-50%);
  z-index: 32;
  pointer-events: none;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  display: none;
  animation: tutorial-in 280ms ease-out;
}
#${ROOT_ID}.tutorial-root--out {
  animation: tutorial-out 320ms ease-in forwards;
}
.tutorial-card {
  pointer-events: auto;
  width: min(92vw, 460px);
  padding: 12px 18px 10px;
  border-radius: 8px;
  background: rgba(12, 14, 18, 0.96);
  border: 1px solid rgba(160, 200, 255, 0.45);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.55), 0 0 18px rgba(105, 180, 255, 0.18);
  color: #d4dae2;
}
.tutorial-card__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}
.tutorial-card__chip {
  color: #a9c8ff;
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  background: rgba(105, 180, 255, 0.12);
  padding: 3px 8px;
  border-radius: 3px;
}
.tutorial-card__skip {
  font: inherit;
  font-size: 10px;
  color: #8a92a0;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.tutorial-card__skip:hover { color: #d4dae2; }
/* V1.9 — passive macro toast variant. Compact, lower-contrast border, no
 * progress counter, dismiss "x" instead of Skip. Sits at the bottom of
 * the play area without claiming the player's full attention. */
.tutorial-card--passive {
  width: min(82vw, 360px);
  padding: 9px 12px 8px;
  border-color: rgba(106, 216, 255, 0.25);
  background: rgba(12, 14, 18, 0.86);
}
.tutorial-card--passive .tutorial-card__title { font-size: 12px; }
.tutorial-card--passive .tutorial-card__body { font-size: 11px; line-height: 1.5; }
.tutorial-card__close {
  font: inherit;
  font-size: 14px;
  line-height: 1;
  color: #6c7480;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0 6px;
}
.tutorial-card__close:hover { color: #d4dae2; }
.tutorial-card__title {
  font-size: 14px;
  font-weight: 700;
  color: #eef3f8;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}
.tutorial-card__body {
  font-size: 12px;
  color: #b8c0cc;
  line-height: 1.55;
}
.tutorial-card__progress {
  margin-top: 8px;
  font-size: 10px;
  color: #6c7480;
  letter-spacing: 0.1em;
  text-align: right;
}
@keyframes tutorial-in {
  0%   { opacity: 0; transform: translate(-50%, 16px); }
  100% { opacity: 1; transform: translate(-50%, 0); }
}
@keyframes tutorial-out {
  0%   { opacity: 1; }
  100% { opacity: 0; transform: translate(-50%, 16px); }
}
@media (max-width: 820px), (pointer: coarse) {
  #${ROOT_ID} { bottom: 88px; }
}
  `;
  document.head.appendChild(style);
  injectHudThemeStyles();
}
